/**
 * EO «Конфигуратор → Квесты и слоты» — панель суб-таба по эталону
 * design-references/screen-17-eo-config-quests-slots.html + screen-17-quests.js.
 *
 * BranchTabs → шапка «Квесты: {филиал} (N)» + Добавить/Отмена (ActionPair-solo)
 * → форма создания (.cform) → аккордеон карточек (.qcard): collapsed-строка
 * (QuestCardRow) + expanded-тело (QuestCardBody). Удаление — double-confirm
 * через CfgModal ×2 стадии (js:358-371). Несохранённая сетка (draft слотов) —
 * guard смены филиала здесь + onDirtyChange для guard'а суб-табов в
 * EoConfigView. Скелетон — только при реальной загрузке. Драфт формы (fdraft)
 * один на экран — collapsed-строка открытой карточки показывает связи из него
 * (эталон js:208). Жесты A43: свайп-вправо по collapsed-карточке — форма
 * создания с данными исходника (клон в БД по «Создать»); pull-to-refresh.
 */
import { useEffect, useState } from "react";
import type { Quest } from "../../api/types";
import { BranchTabs } from "../../components/grid";
import { PullHud } from "../../components/shared";
import { useAppData } from "../../hooks/useAppData";
import { useAppReload, usePullToRefresh } from "../../hooks/usePullToRefresh";
import { consWire } from "../../hooks/useQuestCats";
import { useQuestsAdmin } from "../../hooks/useQuestsAdmin";
import { CfgModal } from "./EoConfigModal";
import { opsOfQuest, opsToCrew } from "./OpsRow";
import {
  initDraft, QuestCardBody, QuestCreateForm,
  type QuestCreateDraft, type QuestFormDraft,
} from "./QuestCardForm";
import { QuestCardRow } from "./QuestCardRow";
import { CrossIco, PlusIco } from "./questIcons";
import { SwipeDup } from "./SwipeDup";
import cfg from "./config.module.css";
import css from "./quests.module.css";

const C0: QuestCreateDraft = { name: "", short: "", dur: 60, reset: 15, ops: ["a"] };

export function EoConfigQuests({ onDirtyChange }: { onDirtyChange: (d: boolean, label?: string) => void }) {
  const app = useAppData();
  const [branchId, setBranchId] = useState("");
  const qa = useQuestsAdmin(branchId);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [fdraft, setFdraft] = useState<QuestFormDraft | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string[]>>({});
  const [secOpen, setSecOpen] = useState<Record<string, boolean>>({});
  const [cOpen, setCOpen] = useState(false);
  const [cBusy, setCBusy] = useState(false);
  const [cForm, setCForm] = useState<QuestCreateDraft>(C0);
  const [del, setDel] = useState<{ q: Quest; stage: 0 | 1 } | null>(null);
  const [pendingBranch, setPendingBranch] = useState<string | null>(null);
  /* Исходник свайп-дубликата: форма создания предзаполнена им (A43.1). */
  const [cloneSrc, setCloneSrc] = useState<Quest | null>(null);
  const reloadApp = useAppReload();
  const ptr = usePullToRefresh(() => { qa.refetch(); return reloadApp(); });

  /* Дефолт — первый активный филиал (паттерн EoConfigBranches). */
  useEffect(() => {
    if (!branchId) {
      const first = app.branches.find((x) => x.active);
      if (first) setBranchId(first.id);
    }
  }, [app.branches, branchId]);

  const dirtyQ = qa.quests.find((q) => drafts[q.id]) ?? null;
  const dirty = !!dirtyQ;
  const dirtyName = dirtyQ?.name;
  /* Подпись — имя квеста: guard суб-табов называет сетку (эталон js:95). */
  useEffect(() => { onDirtyChange(dirty, dirtyName); }, [dirty, dirtyName, onDirtyChange]);

  const branch = app.branches.find((b) => b.id === branchId) ?? null;

  const switchTo = (id: string) => {
    setBranchId(id); setExpanded(null); setFdraft(null); setDrafts({}); setCOpen(false); setCloneSrc(null);
  };
  const pickBranch = (id: string) => {
    if (id === branchId) return;
    if (dirty) setPendingBranch(id);
    else switchTo(id);
  };

  const setGrid = (qid: string, next: string[] | null) =>
    setDrafts((d) => {
      const n = { ...d };
      if (next) n[qid] = next; else delete n[qid];
      return n;
    });

  const toggleCard = (q: Quest) => {
    if (expanded === q.id) { setExpanded(null); setFdraft(null); }
    else { setExpanded(q.id); setFdraft(initDraft(q, qa)); }
  };

  const toggleCreate = () => { setCOpen((o) => !o); setCBusy(false); setCForm(C0); setCloneSrc(null); };

  /* A43.1: свайп-вправо — форма создания, предзаполненная исходником. */
  const dupQuest = (q: Quest) => {
    qa.loadCons(q.id); // прогрев кеша — фолбэк consFor при ошибке сети
    setCloneSrc(q);
    /* slice: sanitize name бросает 400 при >200 — длинный исходник + «(копия)» */
    setCForm({ name: `${q.name} (копия)`.slice(0, 200), short: q.short_name, dur: q.duration, reset: q.reset_time, ops: opsOfQuest(q) });
    setCBusy(false); setCOpen(true);
  };

  const submitCreate = () => {
    if (cBusy || !cForm.name.trim()) return;
    const name = cForm.name.trim();
    const short = cForm.short.trim() || name.slice(0, 8);
    const src = cloneSrc; setCBusy(true);
    /* Нормы исходника — явный GET параллельно POST, кеш — фолбэк сети (race: прогрев dupQuest мог не успеть). */
    const consP = src ? qa.consFor(src.id) : null;
    void qa.createQuest({ name, short, dur: cForm.dur, reset: cForm.reset, crew: opsToCrew(cForm.ops) })
      .then(async (id) => {
        if (!id) { setCBusy(false); return; }
        /* Клон: расходники/ресурсы/цены/связи исходника; слоты НЕ копируются. */
        const cons = consP ? consWire(await consP) : null;
        setCBusy(false); setCOpen(false); setCForm(C0); setCloneSrc(null);
        const p = src ? qa.prices[src.id] : undefined;
        const d: QuestFormDraft = {
          id, name, short, dur: cForm.dur, reset: cForm.reset, ops: [...cForm.ops],
          cons, links: src ? qa.linksOf(src.id) : [], res: src ? qa.questRes[src.id] ?? [] : [],
          pn: p?.threshold ?? 4, pb: p?.base_price ?? 0, pp: p?.extra_per_player ?? 0, mk: "", // mk_id клону не наследуется
        };
        if (src) {
          /* Полный клон в БД по «Создать»: saveQuest вдогонку POST. */
          const nq: Quest = {
            id, branch_id: branchId, name, short_name: short, duration: cForm.dur,
            reset_time: cForm.reset, operators: cForm.ops.length || 1, active: 1,
          };
          void qa.saveQuest(nq, {
            name, short_name: short, duration: cForm.dur, reset_time: cForm.reset,
            crew_rule: opsToCrew(cForm.ops), cons: cons?.length ? cons : null,
            res: d.res, links: d.links, pn: d.pn, pb: d.pb, pp: d.pp, min_price: p?.min_price,
          });
        }
        setExpanded(id);
        setFdraft(d);
      });
  };

  /* «Развернуть всё» открытой карточки: 4 ключа секций инвертируются разом. */
  const expAll = (q: Quest) => {
    const keys = ["cons", "res", "mk", "wg"].map((s) => `${q.id}:${s}`);
    const allOpen = keys.every((k) => secOpen[k]);
    setSecOpen((s) => {
      const n = { ...s };
      for (const k of keys) n[k] = !allOpen;
      return n;
    });
  };

  if (qa.loading) {
    return (
      <div className={cfg.scroll}>
        <div className={cfg.skel}>
          <div className={`${cfg.sk} ${cfg.skTitle}`} />
          {[0, 1, 2, 3, 4, 5].map((i) => <div key={i} className={`${cfg.sk} ${cfg.skTea}`} />)}
        </div>
      </div>
    );
  }

  return (
    <>
      {/* data-cfg-guard: смену филиала при dirty решает confirm-модалка. */}
      <div data-cfg-guard="">
        <BranchTabs
          branches={app.branches.filter((x) => x.active)}
          active={branchId}
          onChange={pickBranch}
          allChip={false}
          accent="role"
        />
      </div>
      <div className={cfg.scroll} ref={ptr.ref}>
        <PullHud pull={ptr.pull} pulling={ptr.pulling} refreshing={ptr.refreshing} />
        <div className={css.s17head}>
          <h2 className={cfg.secT}>Квесты: {branch?.name ?? "—"} ({qa.quests.length})</h2>
          <button type="button" className={`${css.abtn} ${css.cf} ${css.solo}`} onClick={toggleCreate}>
            {cOpen ? <><CrossIco w={11} />Отмена</> : <><PlusIco />Добавить</>}
          </button>
        </div>
        {qa.hint ? <div className={cfg.errHint} style={{ margin: "0 8px" }}>{qa.hint}</div> : null}
        {cOpen ? (
          <QuestCreateForm f={cForm} setF={setCForm} busy={cBusy} onCreate={submitCreate} onCancel={toggleCreate} />
        ) : null}
        {qa.quests.map((q) => {
          const open = expanded === q.id;
          const grid = drafts[q.id] ?? qa.slotsOf(q.id);
          const allOpen = ["cons", "res", "mk", "wg"].every((s) => secOpen[`${q.id}:${s}`]);
          const links = open && fdraft?.id === q.id ? fdraft.links : qa.linksOf(q.id);
          const f = qa.flash?.key === `card:${q.id}` ? qa.flash : null;
          return (
            <article
              key={f ? `${q.id}:${f.n}` : q.id}
              className={`${css.qcard} ${drafts[q.id] ? css.dirty : ""} ${open ? css.open : ""} ${f ? cfg.flash : ""}`}
            >
              <SwipeDup disabled={open} onDup={() => dupQuest(q)} className={css.qrow} onClick={() => toggleCard(q)}>
                <QuestCardRow
                  q={q} qa={qa} open={open} allOpen={allOpen} links={links} grid={grid}
                  onExpAll={() => expAll(q)} onDel={() => setDel({ q, stage: 0 })}
                />
              </SwipeDup>
              {open && fdraft?.id === q.id ? (
                <QuestCardBody
                  q={q} branch={branch} qa={qa} draft={fdraft} setDraft={setFdraft}
                  onCancel={() => setFdraft(initDraft(q, qa))}
                  grid={drafts[q.id] ?? null} onGrid={(t) => setGrid(q.id, t)}
                  secOpen={secOpen} onSec={(k) => setSecOpen((s) => ({ ...s, [k]: !s[k] }))}
                />
              ) : null}
            </article>
          );
        })}
        {!qa.quests.length ? <div className={cfg.teaEmpty}>Нет квестов — добавьте первый.</div> : null}
      </div>
      {del ? (
        <CfgModal
          tone="danger"
          title={del.stage === 0 ? "Удалить квест?" : "Точно удалить?"}
          actionLabel={del.stage === 0 ? "Удалить" : "Точно удалить"}
          onAction={() => {
            if (del.stage === 0) { setDel({ q: del.q, stage: 1 }); return; }
            qa.deleteQuest(del.q);
            if (expanded === del.q.id) { setExpanded(null); setFdraft(null); }
            setGrid(del.q.id, null);
            setDel(null);
          }}
          onCancel={() => setDel(null)}
        >
          {del.stage === 0
            ? <>«<b>{del.q.name}</b>» будет удалён из конфигуратора филиала.</>
            : <>
                Все слоты «<b>{del.q.name}</b>» исчезнут из сетки бронирования.
                <span className={cfg.mSub}>Исторические брони и отчёты останутся.</span>
              </>}
        </CfgModal>
      ) : null}
      {pendingBranch ? (
        <CfgModal
          tone="warn"
          title="Сетка не сохранена"
          actionLabel="Выйти"
          onAction={() => { switchTo(pendingBranch); setPendingBranch(null); }}
          onCancel={() => setPendingBranch(null)}
        >
          Сетка «<b>{dirtyQ?.name}</b>» не сохранена — выйти без сохранения?
        </CfgModal>
      ) : null}
    </>
  );
}
